import React from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Divider,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    Avatar,
    Grid,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    InputAdornment,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    useTheme
} from '@mui/material';
import {
    ExpandMore,
    Description as DescriptionIcon,
    Straighten as StraightenIcon,
    RequestQuote as RequestQuoteIcon,
    Extension as ExtensionIcon
} from '@mui/icons-material';
import { Window, MaterialType } from '../services/windowsApi';
import { Door } from '../services/doorsApi';
import { Profile } from '../services/profilesApi';
import { ComponentItem } from './ComponentLibrary';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import { useSystemParameters } from '../../Commun/contexts/SystemParametersContext';

interface DesignSheetProps {
    item: Window | Door | null;
    components: ComponentItem[];
    onUpdateDimensions: (width: number, height: number) => void;
    onUpdateQuotation: (field: string, value: any) => void;
    onUpdateItem?: (updates: Partial<Window | Door>) => void;
    quotation: {
        unitPrice?: number;
        quantity?: number;
        discount?: number;
        notes?: string;
    };
    hybridMaterials?: { core: string; cladding: string; interior: string; hardware: string };
    surfaceMaterial?: string;
    onUpdateHybridMaterials?: (hybridMaterials: any) => void;
    onUpdateSurfaceMaterial?: (surfaceMaterial: string) => void;
    profiles: Profile[];
}

const DesignSheet: React.FC<DesignSheetProps> = ({
    item,
    components,
    onUpdateDimensions,
    onUpdateQuotation,
    onUpdateItem,
    quotation,
    hybridMaterials,
    surfaceMaterial,
    onUpdateHybridMaterials,
    onUpdateSurfaceMaterial,
    profiles,
}) => {
    const { t } = useLanguage();
    const { getParam } = useSystemParameters();
    const currencySymbol = getParam('Currency_Symbol', 'DZD');
    const theme = useTheme();

    if (!item) {
        return (
            <Paper
                elevation={0}
                sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 4,
                    bgcolor: theme.palette.background.default,
                    color: theme.palette.text.secondary
                }}
            >
                <DescriptionIcon sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
                <Typography variant="h6" gutterBottom>
                    {t('maker.design.sheet.empty.title')}
                </Typography>
                <Typography variant="body2" align="center">
                    {t('maker.design.sheet.empty.desc')}
                </Typography>
            </Paper>
        );
    }

    const isWindow = 'windowType' in item;
    const totalComponentCost = components.reduce((sum, comp) => {
        const qty = comp.defaultQuantity || 1;
        return sum + (comp.unitPrice * qty);
    }, 0);

    const basePrice = quotation.unitPrice || 0;
    const quantity = quotation.quantity || 1;
    const discount = quotation.discount || 0;
    const subtotal = (basePrice + totalComponentCost) * quantity;
    const discountAmount = subtotal * (discount / 100);
    const total = subtotal - discountAmount;

    return (
        <Box sx={{ height: '100%', overflowY: 'auto', bgcolor: 'background.paper' }}>
            {/* General Information */}
            <Accordion defaultExpanded square disableGutters sx={{ borderBottom: 1, borderColor: 'divider', '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="subtitle2" fontWeight="bold">General Information</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 1, pb: 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField fullWidth label="Code" value={`DSN-${item.id}`} size="small" InputProps={{ readOnly: true }} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth label="Designation" value={isWindow ? (item as any).name || 'Window' : (item as any).name || 'Door'} size="small" />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth multiline rows={2} label="Description" value={quotation.notes || ''} onChange={(e) => onUpdateQuotation('notes', e.target.value)} size="small" />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth type="number" label="Quantity" value={quotation.quantity || 1} onChange={(e) => onUpdateQuotation('quantity', parseInt(e.target.value) || 1)} size="small" />
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>

            {/* Material Information */}
            <Accordion defaultExpanded square disableGutters sx={{ borderBottom: 1, borderColor: 'divider', '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="subtitle2" fontWeight="bold">Material Information</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 1, pb: 2 }}>
                    <Grid container spacing={2}>

                        <Grid item xs={12}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Profile System</InputLabel>
                                <Select
                                    value={item.materialType}
                                    label="Profile System"
                                    onChange={(e) => onUpdateItem && onUpdateItem({ materialType: Number(e.target.value), profileId: undefined })}
                                    readOnly={!onUpdateItem}
                                >
                                    <MenuItem value={MaterialType.Aluminum}>Aluminum</MenuItem>
                                    <MenuItem value={MaterialType.PVC}>PVC</MenuItem>
                                    <MenuItem value={MaterialType.Wood}>Wood</MenuItem>
                                    <MenuItem value={MaterialType.Steel}>Steel</MenuItem>
                                    <MenuItem value={MaterialType.Hybrid}>Hybrid (Multi-layer)</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth size="small" disabled={!item.materialType || !onUpdateItem}>
                                <InputLabel>Profile Series</InputLabel>
                                <Select
                                    value={item.profileId ? item.profileId.toString() : ''}
                                    label="Profile Series"
                                    onChange={(e) => onUpdateItem && onUpdateItem({ profileId: Number(e.target.value) })}
                                    readOnly={!onUpdateItem}
                                >
                                    <MenuItem value=""><em>None</em></MenuItem>
                                    {profiles
                                        .filter(p => p.materialType === item.materialType)
                                        .map((profile) => (
                                            <MenuItem key={profile.id} value={profile.id.toString()}>
                                                {profile.name} ({profile.code})
                                            </MenuItem>
                                        ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField fullWidth label="Color" value="White" size="small" />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField fullWidth label="Plating" value="Standard" size="small" />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Surface Material (Glazing / Panel)</InputLabel>
                                <Select
                                    value={surfaceMaterial || 'ClearGlass'}
                                    label="Surface Material (Glazing / Panel)"
                                    onChange={(e) => onUpdateSurfaceMaterial && onUpdateSurfaceMaterial(e.target.value)}
                                >
                                    <MenuItem value="ClearGlass">Clear Glass</MenuItem>
                                    <MenuItem value="FrostedGlass">Frosted Glass</MenuItem>
                                    <MenuItem value="TintedBronze">Tinted Glass (Bronze)</MenuItem>
                                    <MenuItem value="TintedBlue">Tinted Glass (Blue)</MenuItem>
                                    <MenuItem value="ResinClear">Resin / Acrylic (Clear)</MenuItem>
                                    <MenuItem value="ResinOpaque">Resin / Acrylic (Opaque)</MenuItem>
                                    <MenuItem value="SolidPanel">Solid Panel</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        {item.materialType === MaterialType.Hybrid && hybridMaterials && onUpdateHybridMaterials && (
                            <Grid item xs={12}>
                                <Paper variant="outlined" sx={{ p: 2, mt: 1, bgcolor: 'background.default' }}>
                                    <Typography variant="subtitle2" gutterBottom fontWeight="bold" color="primary.main">Hybrid Layer Configuration</Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <TextField fullWidth label="Core Material" value={hybridMaterials.core} onChange={(e) => onUpdateHybridMaterials({ ...hybridMaterials, core: e.target.value })} size="small" />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField fullWidth label="Exterior Cladding" value={hybridMaterials.cladding} onChange={(e) => onUpdateHybridMaterials({ ...hybridMaterials, cladding: e.target.value })} size="small" />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField fullWidth label="Interior Finish" value={hybridMaterials.interior} onChange={(e) => onUpdateHybridMaterials({ ...hybridMaterials, interior: e.target.value })} size="small" />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField fullWidth label="Hardware / Reinforcement" value={hybridMaterials.hardware} onChange={(e) => onUpdateHybridMaterials({ ...hybridMaterials, hardware: e.target.value })} size="small" />
                                        </Grid>
                                    </Grid>
                                </Paper>
                            </Grid>
                        )}
                    </Grid>
                </AccordionDetails>
            </Accordion>

            {/* Dimensions */}
            <Accordion defaultExpanded square disableGutters sx={{ borderBottom: 1, borderColor: 'divider', '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="subtitle2" fontWeight="bold">Dimensions</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 1, pb: 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <TextField
                                label="Width (mm)"
                                type="number"
                                value={item.width}
                                onChange={(e) => onUpdateDimensions(parseFloat(e.target.value) || 0, item.height)}
                                fullWidth
                                size="small"
                                InputProps={{ inputProps: { min: 1 } }}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                label="Height (mm)"
                                type="number"
                                value={item.height}
                                onChange={(e) => onUpdateDimensions(item.width, parseFloat(e.target.value) || 0)}
                                fullWidth
                                size="small"
                                InputProps={{ inputProps: { min: 1 } }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary">Area: {((item.width * item.height) / 1000000).toFixed(2)} m²</Typography>
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>

            {/* Components / Hardware */}
            <Accordion square disableGutters sx={{ borderBottom: 1, borderColor: 'divider', '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="subtitle2" fontWeight="bold">Components & Hardware</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 1, pb: 2 }}>
                    {components.length === 0 ? (
                        <Typography variant="caption" color="text.secondary">No components added.</Typography>
                    ) : (
                        <List dense>
                            {components.map((comp) => (
                                <ListItem key={comp.id} disableGutters>
                                    <ListItemAvatar>
                                        <Avatar sx={{ bgcolor: theme.palette.primary.light, width: 32, height: 32, fontSize: '1rem' }}>
                                            {typeof comp.icon === 'string' ? comp.icon : 'C'}
                                        </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText primary={comp.name} secondary={`x${comp.defaultQuantity || 1}`} />
                                    <Typography variant="caption">${comp.unitPrice}</Typography>
                                </ListItem>
                            ))}
                        </List>
                    )}
                </AccordionDetails>
            </Accordion>

            {/* Costing */}
            <Accordion square disableGutters sx={{ borderBottom: 1, borderColor: 'divider', '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="subtitle2" fontWeight="bold">Costing</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 1, pb: 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                label="Unit Price"
                                type="number"
                                value={quotation.unitPrice || ''}
                                onChange={(e) => onUpdateQuotation('unitPrice', parseFloat(e.target.value) || 0)}
                                fullWidth
                                size="small"
                                InputProps={{ startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment> }}
                            />
                        </Grid>

                        <Paper variant="outlined" sx={{ p: 2, bgcolor: theme.palette.action.hover, width: '100%', mt: 2 }}>
                            <Grid container spacing={1}>
                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">Base Price:</Typography>
                                </Grid>
                                <Grid item xs={6} textAlign="right">
                                    <Typography variant="body2">{currencySymbol}{basePrice.toFixed(2)}</Typography>
                                </Grid>

                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">Components:</Typography>
                                </Grid>
                                <Grid item xs={6} textAlign="right">
                                    <Typography variant="body2">{currencySymbol}{totalComponentCost.toFixed(2)}</Typography>
                                </Grid>

                                <Grid item xs={6}>
                                    <Typography variant="body2" color="text.secondary">Subtotal ({quantity}×):</Typography>
                                </Grid>
                                <Grid item xs={6} textAlign="right">
                                    <Typography variant="body2" fontWeight="bold">{currencySymbol}{subtotal.toFixed(2)}</Typography>
                                </Grid>

                                <Grid item xs={12} sx={{ my: 1 }}><Divider /></Grid>

                                <Grid item xs={6}>
                                    <Typography variant="subtitle1" fontWeight="bold">Total:</Typography>
                                </Grid>
                                <Grid item xs={6} textAlign="right">
                                    <Typography variant="subtitle1" fontWeight="bold" color="primary">{currencySymbol}{total.toFixed(2)}</Typography>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>
                </AccordionDetails>
            </Accordion>
        </Box>
    );
};

export default DesignSheet;
